/**
 * Lo que el USUARIO ve del acceso de soporte con permiso. Tres piezas:
 *
 * 1. **Modal de permiso**, que no se puede cerrar: ni con Escape ni con un clic
 *    fuera. La única salida es contestar (o que caduque la solicitud). Es una
 *    pregunta sobre su cuenta, y cerrarla sin contestar dejaría al admin
 *    esperando sin saber nada.
 * 2. **Banner superior** mientras el soporte está dentro, con «Retirar acceso».
 * 3. **Aviso de salida**: el banner pasa a decir que el soporte ha salido, y
 *    desaparece a los 5 s.
 *
 * Se monta en `App.jsx`, al lado de `ImpersonationBar` y fuera de `<Routes>`,
 * por lo mismo que ella: tiene que verse en cualquier pantalla.
 *
 * El aviso de salida sale por dos caminos y los dos hacen falta: la transición
 * activo → no activo que esta pestaña VE (lo normal), y `endedRecently` del
 * backend, para la pestaña que se abre o se recarga justo después de la salida.
 * Un conjunto de ids ya enseñados evita repetirlo.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Headset, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';

import { apiMessage } from '@/api/apiMessage';
import { respondSupportAccess, revokeSupportAccess } from '@/api/supportAccessService';
import { useLanguage } from '@/components/LanguageContext';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { SUPPORT_ACCESS_QUERY_KEY, useSupportAccess } from '@/hooks/useSupportAccess';

export const EXIT_NOTICE_MS = 5000;

function useSecondsLeft(deadline) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!deadline) {
      setLeft(null);
      return undefined;
    }
    const target = new Date(deadline).getTime();
    if (Number.isNaN(target)) return undefined;
    const tick = () => setLeft(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return left;
}

function exitMessageKey(reason) {
  if (reason === 'revoked') return 'supportAccess.user.exitedRevoked';
  if (reason === 'expired' || reason === 'inactive') return 'supportAccess.user.exitedExpired';
  return 'supportAccess.user.exited';
}

export default function SupportAccessGate() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { data } = useSupportAccess();

  const pending = data?.pending || null;
  const active = data?.active || null;

  // ── Modal ────────────────────────────────────────────────────────────────
  // Ids ya contestados desde esta pestaña: el modal se cierra en el acto, sin
  // esperar a la siguiente consulta (que podría tardar hasta 5 s).
  const [answered, setAnswered] = useState(() => new Set());
  const [answering, setAnswering] = useState(null); // 'accept' | 'deny' | null
  const secondsLeft = useSecondsLeft(pending?.deadlineAt);
  const modalOpen = !!pending && !answered.has(pending.id) && secondsLeft !== 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: SUPPORT_ACCESS_QUERY_KEY });

  const answer = async (accept) => {
    if (!pending || answering) return;
    setAnswering(accept ? 'accept' : 'deny');
    try {
      await respondSupportAccess(pending.id, accept);
      toast.success(t(accept ? 'supportAccess.user.grantedToast' : 'supportAccess.user.deniedToast'));
    } catch (err) {
      // Ya contestada en otra pestaña o caducada: se dice y se cierra igual.
      toast.error(apiMessage(err, t, t('supportAccess.user.answerError')));
    } finally {
      setAnswered((prev) => new Set(prev).add(pending.id));
      setAnswering(null);
      refresh();
    }
  };

  // ── Banner y aviso de salida ─────────────────────────────────────────────
  const [exitNotice, setExitNotice] = useState(null); // { id, reason }
  const shownExits = useRef(new Set());
  const lastActive = useRef(null);

  useEffect(() => {
    if (!data) return;
    const showExit = (id, reason) => {
      if (!id || shownExits.current.has(id)) return;
      shownExits.current.add(id);
      setExitNotice({ id, reason });
    };
    if (data.active) {
      lastActive.current = data.active.id;
      setExitNotice(null);
    } else if (data.endedRecently) {
      showExit(data.endedRecently.id, data.endedRecently.reason);
      lastActive.current = null;
    } else if (lastActive.current) {
      showExit(lastActive.current, null);
      lastActive.current = null;
    }
  }, [data]);

  useEffect(() => {
    if (!exitNotice) return undefined;
    const id = setTimeout(() => setExitNotice(null), EXIT_NOTICE_MS);
    return () => clearTimeout(id);
  }, [exitNotice]);

  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const revoke = async () => {
    if (!active || revoking) return;
    setRevoking(true);
    try {
      await revokeSupportAccess(active.id);
      // El aviso «ha salido» lo pinta el efecto de arriba al refrescar; aquí
      // se marca ya con su motivo para no esperar a la consulta.
      shownExits.current.add(active.id);
      lastActive.current = null;
      setExitNotice({ id: active.id, reason: 'revoked' });
    } catch (err) {
      toast.error(apiMessage(err, t, t('supportAccess.user.revokeError')));
    } finally {
      setRevoking(false);
      setConfirmRevoke(false);
      refresh();
    }
  };

  const showBanner = !!active && !exitNotice;

  return (
    <>
      {showBanner && (
        <>
          <div role="status" className="fixed top-0 inset-x-0 z-[100] shadow-md bg-sky-600 text-white">
            <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-3 text-sm">
              <Headset className="w-4 h-4 shrink-0" />
              <span className="font-semibold truncate">{t('supportAccess.user.bannerActive')}</span>
              <button
                type="button"
                onClick={() => setConfirmRevoke(true)}
                disabled={revoking}
                className="ml-auto shrink-0 inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 font-semibold text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
              >
                {revoking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                {t('supportAccess.user.revoke')}
              </button>
            </div>
          </div>
          <div className="h-11" aria-hidden="true" />
        </>
      )}

      {exitNotice && (
        <>
          <div role="status" className="fixed top-0 inset-x-0 z-[100] shadow-md bg-slate-800 text-white">
            <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-3 text-sm">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span className="font-semibold truncate">{t(exitMessageKey(exitNotice.reason))}</span>
            </div>
          </div>
          <div className="h-11" aria-hidden="true" />
        </>
      )}

      {/* Controlado y sin `onOpenChange`: solo lo cierra una respuesta. Radix ya
          ignora el clic fuera en un AlertDialog; Escape hay que pararlo a mano. */}
      <AlertDialog open={modalOpen}>
        <AlertDialogContent
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="support-access-request-modal"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Headset className="w-5 h-5 text-sky-600" />
              {t('supportAccess.user.modalTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>{t('supportAccess.user.modalBody', { minutes: pending?.maxMinutes ?? 60 })}</p>
                <p>{t('supportAccess.user.modalRevokeHint')}</p>
                {secondsLeft != null && (
                  <p className="text-xs text-slate-400 tabular-nums">
                    {t('supportAccess.user.modalExpiresIn', { seconds: secondsLeft })}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <Button variant="outline" onClick={() => answer(false)} disabled={!!answering}>
              {answering === 'deny' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('supportAccess.user.deny')}
            </Button>
            <Button onClick={() => answer(true)} disabled={!!answering}>
              {answering === 'accept' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('supportAccess.user.accept')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmación de «Retirar acceso». Esta sí se puede cerrar. */}
      <AlertDialog open={confirmRevoke} onOpenChange={(o) => !revoking && setConfirmRevoke(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('supportAccess.user.revokeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('supportAccess.user.revokeConfirmBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>{t('supportAccess.user.revokeCancel')}</AlertDialogCancel>
            <Button variant="destructive" onClick={revoke} disabled={revoking}>
              {revoking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('supportAccess.user.revokeConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
